import React, { useState } from 'react';
import api, { errorMessage } from '../api';
import { useAuth } from '../context/AuthContext';
import { LocateIcon } from './icons';
import { locationHelp } from '../lib/platform';

/**
 * Frågan om platsdelning, en gång per konto efter första inloggningen.
 *
 * Ordningen är medveten: användaren får veta vad delningen innebär innan
 * webbläsarens systemdialog dyker upp. Dialogen utlöses först när man
 * tryckt på "Ja", dels för att det är ärligare, dels för att ett nej i
 * webbläsaren ofta är permanent — den frågan ska inte slösas bort på en
 * inloggningssida där man inte förstår vad den gäller.
 */
export default function LocationPrompt({ onDone }) {
  const { setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const markAnswered = async () => {
    try {
      const res = await api.post('/user/location-prompt-answered');
      setUser(res.data);
    } catch {
      // Inte kritiskt — då får man frågan igen nästa gång.
    }
  };

  /** Ber webbläsaren om tillstånd och slår på delningen om det gick. */
  const requestPermission = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(false);
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      () => resolve(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });

  const accept = async () => {
    setBusy(true);
    const granted = await requestPermission();

    if (!granted) {
      // Tillståndet nekades i webbläsaren. Att slå på delningen ändå vore
      // att lova något appen inte kan hålla.
      setDenied(true);
      setBusy(false);
      await markAnswered();
      return;
    }

    try {
      const res = await api.put('/user/settings', {
        share_location: true,
        share_destination: false,
      });
      setUser(res.data);
      await markAnswered();
      onDone();
    } catch (err) {
      setDenied(false);
      setBusy(false);
      // eslint-disable-next-line no-alert
      alert(errorMessage(err, 'Kunde inte spara inställningen.'));
    }
  };

  const decline = async () => {
    setBusy(true);
    await markAnswered();
    onDone();
  };

  if (denied) {
    const help = locationHelp();
    return (
      <div className="sheet-overlay">
        <div className="sheet">
          <span className="sheet__icon"><LocateIcon size={28} /></span>
          <h2 className="sheet__title">{help.title}</h2>
          <p className="sheet__text">
            Webbläsaren säger nej till platsåtkomst, så vi kan inte visa var du är.
          </p>
          <ol className="sheet__steps">
            {help.steps.map(step => <li key={step}>{step}</li>)}
          </ol>
          <button className="btn btn--primary btn--block" onClick={onDone}>Okej</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sheet-overlay">
      <div className="sheet">
        <span className="sheet__icon"><LocateIcon size={28} /></span>
        <h2 className="sheet__title">Ska dina vänner se var du är?</h2>
        <p className="sheet__text">
          Då syns du på kartan när ni är ute och kör, och du ser dem.
        </p>

        <ul className="sheet__list">
          <li>Bara vänner du själv har accepterat ser din plats.</li>
          <li>Du syns bara medan appen är öppen.</li>
          <li>Du kan stänga av det när som helst under <strong>Mer</strong>.</li>
        </ul>

        <button className="btn btn--primary btn--block" onClick={accept} disabled={busy}>
          {busy ? 'Ett ögonblick...' : 'Ja, dela med mina vänner'}
        </button>
        <button className="btn btn--outline btn--block" onClick={decline} disabled={busy}>
          Inte nu
        </button>
      </div>
    </div>
  );
}
